import { useContext } from "react";
import { LangContext } from "../../LangContext";
import Meta from "../common/Meta";
import Bullet from "../common/Bullet";
import Spinner from "../common/Spinner";
import { splitBullets } from "../../lib/utils";

const probLabel = (p, lang) => {
  if (lang !== "en") return p;
  return p === "높음" ? "High" : p === "중간" ? "Medium" : p === "낮음" ? "Low" : p;
};

export default function DeeperTab({
  deepLoading, deepError, ach, cuiBono, scenarios, topArticles,
  subScenariosMap, subLoadingIdx, onLoadSubScenarios,
}) {
  const lang = useContext(LangContext);
  return (
    <div>
      {deepLoading && <Spinner message={lang === "en" ? "Analyzing…" : "심화 분석 중…"} />}

      {deepError && (
        <div className="p-4 border-b border-border-line">
          <div className="text-sub text-tier-gun mb-1.5">{lang === "en" ? "Analysis failed" : "심화 분석 실패"}</div>
          <div className="text-sub text-text-tertiary">{deepError}</div>
        </div>
      )}

      {!deepLoading && ach.length === 0 && scenarios.length === 0 && !deepError && (
        <div className="py-8 px-4 text-center">
          <Meta>{lang === "en" ? "No analysis data — refresh to retry" : "분석 데이터 없음 — 새로고침 후 재시도"}</Meta>
        </div>
      )}

      {/* Scenarios */}
      {scenarios.length > 0 && (
        <div>
          <div className="py-2.5 px-4 flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-tier-watershed flex-shrink-0" />
            <span className="font-sans text-meta font-bold tracking-widest uppercase text-tier-watershed">
              {lang === "en" ? "Scenario Forecast" : "시나리오 예측"}
            </span>
          </div>
          {scenarios.map((s, i) => {
            const probColor = s.probability === "높음" ? "text-dir-escalation" : s.probability === "중간" ? "text-text-secondary" : "text-text-tertiary";
            const subList = subScenariosMap[i];
            const isSubLoading = subLoadingIdx === i;
            return (
              <div key={i} className="p-4 border-b border-border-line animate-fadeUp">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="text-body font-bold text-text-primary leading-snug flex-1">{s.title}</div>
                  <span className={`text-meta font-bold tracking-wide ${probColor}`}>{probLabel(s.probability, lang)}</span>
                </div>
                <div className="mb-2.5">
                  {splitBullets(s.reasoning).map((r, ri) => (
                    <Bullet key={ri} text={r} className="text-sub text-text-secondary leading-relaxed" />
                  ))}
                </div>

                {/* Sub-scenarios */}
                {subList?.map((sub, j) => {
                  const sc = sub.probability === "높음" ? "text-dir-escalation" : sub.probability === "중간" ? "text-text-secondary" : "text-text-tertiary";
                  return (
                    <div key={j} className="mb-2 pl-5">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-text-tertiary text-sub flex-shrink-0">↳</span>
                        <div className="text-sub font-bold text-text-primary flex-1">{sub.title}</div>
                        <span className={`text-[8px] font-bold tracking-wide ${sc}`}>{probLabel(sub.probability, lang)}</span>
                      </div>
                      {sub.detail && <div className="text-sub text-text-secondary leading-snug pl-4">{sub.detail}</div>}
                    </div>
                  );
                })}

                {!subList && !isSubLoading && (
                  <button
                    onClick={() => onLoadSubScenarios(i, s)}
                    className="bg-transparent border border-border-line text-text-tertiary text-meta py-1 px-3 cursor-pointer tracking-wide"
                  >
                    More
                  </button>
                )}
                {isSubLoading && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 border-[1.5px] border-text-tertiary border-t-text-secondary rounded-full animate-spin" />
                    <span className="text-[10px] text-text-tertiary">{lang === "en" ? "Analyzing…" : "분석 중…"}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Framing (ACH) */}
      {ach.length > 0 && (
        <div>
          <div className="py-2.5 px-4 flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-text-secondary flex-shrink-0" />
            <span className="font-sans text-meta font-bold tracking-widest uppercase text-text-secondary">Framing</span>
          </div>
          {ach.map((a, i) => (
            <div key={i} className="p-4 border-b border-border-line animate-fadeUp">
              <Meta className="text-text-secondary">{a.theme || a.lens}</Meta>
              <div className="mt-1.5 mb-2.5 flex flex-col gap-1.5">
                <div className="flex gap-2 items-start">
                  <span className="flex-shrink-0 font-sans text-[8px] font-bold tabular text-dir-deescalation bg-dir-deescalation/10 px-1.5 py-0.5 mt-0.5">A</span>
                  <div className="text-body font-bold text-text-primary leading-snug">{a.sideA || a.hed}</div>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="flex-shrink-0 font-sans text-[8px] font-bold tabular text-dir-escalation bg-dir-escalation/10 px-1.5 py-0.5 mt-0.5">B</span>
                  <div className="text-body font-bold text-text-primary leading-snug">{a.sideB}</div>
                </div>
              </div>
              {(a.evidence || []).map((ev, j) => {
                const evArticle = ev.src ? topArticles.find(ta => ta.src === ev.src) : null;
                const side = ev.side || ev.tag;
                const sideColor = side === "A" ? "text-dir-deescalation bg-dir-deescalation/10" : side === "B" ? "text-dir-escalation bg-dir-escalation/10" : "text-text-secondary bg-bg-secondary";
                return (
                  <div key={j} className="mb-2 pl-2.5 border-l-[1.5px] border-border-line flex gap-2 items-start">
                    {side && (
                      <span className={`flex-shrink-0 font-sans text-[8px] font-bold tabular px-1.5 py-0.5 mt-0.5 ${sideColor}`}>
                        {side}
                      </span>
                    )}
                    <div className="flex-1">
                      <div className="text-sub text-text-secondary leading-relaxed">{ev.text}</div>
                      {ev.src && (
                        evArticle?.url
                          ? <a href={evArticle.url} target="_blank" rel="noopener noreferrer"
                              className="text-meta text-text-tertiary no-underline border-b border-border-line"
                              onClick={e => e.stopPropagation()}>
                              {ev.src}
                            </a>
                          : <span className="text-meta text-text-tertiary">{ev.src}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* 수혜자 분석 */}
      {cuiBono && (
        <div>
          <div className="py-2.5 px-4 flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-text-tertiary flex-shrink-0" />
            <span className="font-sans text-meta font-bold tracking-widest uppercase text-text-tertiary">
              {lang === "en" ? "Who Benefits" : "수혜자 분석"}
            </span>
          </div>
          <div className="p-4 border-b border-border-line animate-fadeUp">
            {typeof cuiBono === "object" ? (
              <div className="flex flex-col gap-1">
                {cuiBono.beneficiary && (
                  <div className="flex gap-1.5 items-start">
                    <span className="text-sub text-text-tertiary flex-shrink-0 mt-0.5">·</span>
                    <span className="text-sub font-bold text-text-primary leading-snug">{cuiBono.beneficiary}</span>
                  </div>
                )}
                {(cuiBono.context || []).map((c, i) => (
                  <div key={i} className="flex gap-1.5 items-start">
                    <span className="text-sub text-text-tertiary flex-shrink-0 mt-0.5">·</span>
                    <span className="text-sub text-text-secondary leading-relaxed">{c}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sub text-text-secondary leading-relaxed">{cuiBono}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
